import React, { FC, useMemo } from 'react';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { useTenderPreview } from 'src/models/procurements/Preview/hooks';
import { Translations } from 'src/lib/I18nService/types';
import { TenderBox, InfoBox, BlockLabel } from 'src/shared';
import { Expand, TenderStatusBadge } from 'src/common';
import TenderPreviewMatchesTotal from '../Matches/MatchesTotal';
import { TenderBlockSize, isFieldArray, TenderBoxMode } from 'src/models/procurements/Tender/types';
import { isAddition } from 'src/models/procurements/Tender/helpers';

export const TenderPreviewInfo: FC = () => {
  const { t } = useTranslation();
  const { t: tenderT } = useTranslation(Translations.procurements);
  const { t: cpvT } = useTranslation(Translations.cpvCodes);

  const { data } = useTenderPreview();
  const boxes = data?.boxes;
  const descBoxes = boxes?.description;
  const introBoxes = boxes?.introduction;
  const scopeBoxes = boxes?.scopeOfContract;
  const durationBoxes = boxes?.contractDurationBoxes;
  const renewalBoxes = boxes?.contractRenewalBoxes;
  const procedureBoxes = boxes?.typeOfProcedureBoxes;

  const estimationValueBox = useMemo(
    () => (boxes?.contractValueBoxes ? boxes.contractValueBoxes[0] : undefined),
    [boxes]
  );
  const deadlineBox = useMemo(() => (boxes?.deadline ? boxes.deadline[0] : undefined), [boxes]);
  const publishedBox = useMemo(() => (boxes?.availableDate ? boxes.availableDate[0] : undefined), [boxes]);

  const locationBoxes = useMemo(
    () =>
      boxes?.locationBoxes
        ? boxes?.placeOfPerformanceNutsCode
          ? [...boxes?.locationBoxes, ...boxes?.placeOfPerformanceNutsCode]
          : [...boxes?.locationBoxes]
        : boxes?.placeOfPerformanceNutsCode
        ? [...boxes.placeOfPerformanceNutsCode]
        : [],
    [boxes]
  );

  const startBox = useMemo(
    () =>
      boxes?.contractStartAndEndStart && !!boxes.contractStartAndEndStart.length
        ? boxes.contractStartAndEndStart[0]
        : undefined,
    [boxes]
  );
  const questionBoxes = boxes?.deadlineOfClarificationQuestions;
  const mainCpvCodes = useMemo(
    () =>
      boxes?.mainCpvCode && isFieldArray(boxes.mainCpvCode[0].firstField)
        ? boxes.mainCpvCode[0].firstField.array
        : undefined,
    [boxes]
  );
  const additionCpvCodes = useMemo(
    () =>
      boxes?.additionalCpvCode && isFieldArray(boxes.additionalCpvCode[0].firstField)
        ? boxes.additionalCpvCode[0].firstField.array
        : undefined,
    [boxes]
  );
  const highlights = data?.highlights;
  const matchedCpvs = data?.matchedCpvs;

  return (
    <>
      {!!data && (
        <>
          {(descBoxes || introBoxes || scopeBoxes) && (
            <Expand visibility={300} isDescription showMoreTitle={t('Common.readMore')}>
              <div className={styles.boxes}>
                {descBoxes && (
                  <TenderBox
                    boxes={descBoxes}
                    title={tenderT('Short description')}
                    as={TenderBoxMode.Collection}
                    actions={false}
                  />
                )}
                {introBoxes && (
                  <TenderBox
                    boxes={introBoxes}
                    title={tenderT('Introduction')}
                    as={TenderBoxMode.Collection}
                    actions={false}
                  />
                )}
                {scopeBoxes && (
                  <TenderBox
                    boxes={scopeBoxes}
                    title={tenderT('Scope of contract')}
                    as={TenderBoxMode.Collection}
                    actions={false}
                  />
                )}
              </div>
            </Expand>
          )}
          <div className={styles.boxes}>
            <TenderBox
              boxes={locationBoxes}
              title={tenderT('Place of performance')}
              as={TenderBoxMode.Collection}
              size={TenderBlockSize.Half}
              actions={false}
            />
            <TenderBox
              box={publishedBox}
              title={tenderT('Publication date')}
              as={TenderBoxMode.Custom}
              size={TenderBlockSize.Half}
              actions={false}
              isFirstField
            />
            <TenderBox
              box={deadlineBox}
              title={
                <>
                  {tenderT('Submission deadline', { context: 'short' })}
                  <TenderStatusBadge
                    mode={'text'}
                    className={styles.tenderStatusBadge}
                    statuses={data.procurementStatus}
                  />
                </>
              }
              as={TenderBoxMode.Custom}
              size={TenderBlockSize.Half}
              actions={false}
              config={{ date: { isCountdown: true } }}
              isFirstField
              isInfo={isAddition(boxes?.deadline)}
            />
            <TenderBox
              box={estimationValueBox}
              title={tenderT('Estimated contract value')}
              as={TenderBoxMode.Custom}
              size={TenderBlockSize.Half}
              actions={false}
              isFirstField
              isInfo={isAddition(boxes?.contractValueBoxes)}
            />
            <TenderBox
              boxes={questionBoxes}
              title={tenderT('Deadline of clarification questions')}
              as={TenderBoxMode.Collection}
              size={TenderBlockSize.Half}
              actions={false}
              config={{ date: { isCountdown: true } }}
            />
            <TenderBox
              boxes={durationBoxes}
              title={tenderT('Contract duration')}
              as={TenderBoxMode.Collection}
              size={TenderBlockSize.Half}
              actions={false}
              isFirstField
              isInfo={isAddition(boxes?.contractDurationBoxes)}
            />
            <TenderBox
              box={startBox}
              title={tenderT('Contract start date')}
              as={TenderBoxMode.Custom}
              size={TenderBlockSize.Half}
              actions={false}
              isFirstField
              isInfo={startBox && isAddition(boxes?.contractStartAndEndStart)}
            />
            <TenderBox
              boxes={renewalBoxes}
              title={tenderT('Contract renewal')}
              as={TenderBoxMode.Collection}
              size={TenderBlockSize.Half}
              actions={false}
              isFirstField
              isInfo={isAddition(boxes?.contractRenewalBoxes)}
            />
            <TenderBox
              boxes={procedureBoxes}
              title={tenderT('Type of procedure')}
              as={TenderBoxMode.Collection}
              size={TenderBlockSize.Half}
              actions={false}
            />
          </div>

          {highlights && (
            <div className={styles.boxes}>
              <InfoBox
                value={<TenderPreviewMatchesTotal highlights={highlights} cpvCodes={matchedCpvs} />}
                size={TenderBlockSize.Full}
              />
            </div>
          )}

          {((mainCpvCodes && mainCpvCodes.length) || (additionCpvCodes && additionCpvCodes.length)) && (
            <div className={styles.boxes}>
              <BlockLabel label={t('Tenders.cpvCodes')} className={styles.title} />
              <Expand visibility={108} isListExpandable showMoreTitle={t('Common.showAll')} isDescription>
                {mainCpvCodes &&
                  mainCpvCodes.length &&
                  mainCpvCodes.map(cpvCode => (
                    <InfoBox
                      key={cpvCode}
                      className={styles.box}
                      title={
                        <>
                          {cpvCode.split('-')[0]}
                          <span className={styles.tag}>{t('Tenders.mainCode')}</span>
                        </>
                      }
                      value={cpvT(cpvCode.split('-')[0])}
                    />
                  ))}
                {additionCpvCodes &&
                  additionCpvCodes.length &&
                  additionCpvCodes.map(cpvCode => (
                    <InfoBox
                      key={cpvCode}
                      className={styles.box}
                      title={cpvCode.split('-')[0]}
                      value={cpvT(cpvCode.split('-')[0])}
                    />
                  ))}
              </Expand>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TenderPreviewInfo;
